<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import CompanyDashboard from '@/components/dashboard/CompanyDashboard.vue'
// TODO(pre-existing): DashboardAccessPanel is not exported by the currently installed
// @bono-packages/dashboard (registry auth expired, package stale). Importing it crashes the
// whole app at boot because HomeView is router-eager. Restore once the package is updated.
import { DashboardView, DashboardEdit, WidgetLibraryDrawer, DashboardChat } from '@bono-packages/dashboard'
import type { Dashboard, DashboardWidget, BuiltQuery, AccessRule, DashboardAskResult } from '@bono-packages/dashboard'
import { useAssistantSurface } from '@bono-packages/chat'
import { SkillsHttpError } from '@bono-packages/skills/runtime'
import { skillsHttp, skillsApiBase } from '@/plugins/skillsPorts'
import { dashboardThread } from '@/plugins/threads'
import { WidgetBuilder } from '@bono-packages/widget'
import { useUserStore } from '@/stores/user'
import { useDashboardStore } from '@/stores/dashboard'
import { useScWidgetStore } from '@/stores/scWidget'
import { useScQueryBuilderStore } from '@/stores/scQueryBuilder'
import { useCurrentUser } from '@/composables/useCurrentUser'
import { useAccessControlUtils } from '@/composables/useAccessControlUtils'
import { hasSelectedCompany } from '@/utils/companyContext'
import type { ResolvedField } from '@bono-packages/query-builder'
import { resolveI18n } from '@/utils/resolveI18n'
import I18nTextInput from '@/components/I18nTextInput.vue'

/** Current locale for localized name display. */
const currentLocale = () => localStorage.getItem('locale') || 'en'

const user = useUserStore()
const route = useRoute()
const dashboardStore = useDashboardStore()
const widgetStore = useScWidgetStore()
const queryBuilderStore = useScQueryBuilderStore()
const { getUserId } = useCurrentUser()
const currentUserId = getUserId()
const { accessControlStore, onApplicationsSelected, onFetchPrincipalValues } = useAccessControlUtils()
const isSuperAdmin = computed(() => user.getUserRoles.some((r: any) => r.roleName === 'BONO_SUPER_ADMIN'))

const CA_SLOT_KEY = 'CA_HOME'

// ── Mode ──────────────────────────────────────────────────────────────────────

const mode = ref<'view' | 'edit'>('view')
const libraryOpen = ref(false)
const dashboardEditRef = ref()
const showMainDashboard = ref(false)

// ── Create dialog ─────────────────────────────────────────────────────────────

const showCreateDialog = ref(false)
const newDashboardName = ref<Record<string, string>>({})

/** Cleaned per-locale name map (drops empty locales); empty = invalid. */
function cleanNameMap(): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(newDashboardName.value)) {
    if (v && v.trim()) out[k] = v.trim()
  }
  return out
}

async function onRenameDashboard({ id, name }: { id: string; name: Record<string, string> }) {
  await dashboardStore.updateDashboard(id, { name })
}

// ── Widget builder (entity fields) ────────────────────────────────────────────

const resolvedFields = ref<ResolvedField[]>([])

async function onEntityChange(entityId: string) {
  resolvedFields.value = await queryBuilderStore.fetchDeepFields(entityId)
}

async function executeSampleQuery(query: BuiltQuery) {
  await queryBuilderStore.executeQuery(query as any)
  const result = queryBuilderStore.queryResults
  return {
    rows: result?.rows ?? [],
    singleValue: result?.singleValue,
    fetchedAt: new Date().toISOString(),
  }
}

// ── Widget data ───────────────────────────────────────────────────────────────

async function fetchWidget(dwId: string, widgetId: string) {
  await dashboardStore.fetchWidgetData(dwId, widgetId)
}

function fetchAllWidgets(dashboard: Dashboard) {
  for (const dw of dashboard.widgets) {
    fetchWidget(dw.id, (dw as any).widgetId ?? dw.widget.id)
  }
}

// ── Available dashboards priority: own personal → own CA_HOME published → others ─

const availableDashboards = computed<Dashboard[]>(() => {
  const all = dashboardStore.dashboards as unknown as Dashboard[]
  const ownPersonal = all.filter(d => d.createdBy === currentUserId && !d.publishedSlots?.length)
  const ownPublished = all.filter(d => d.createdBy === currentUserId && d.publishedSlots?.some(s => s.slotKey === 'CA_HOME'))
  const othersPublished = all.filter(d => d.createdBy !== currentUserId && d.publishedSlots?.some(s => s.slotKey === 'CA_HOME'))
  const seen = new Set<string>()
  const result: Dashboard[] = []
  for (const d of [...ownPersonal, ...ownPublished, ...othersPublished]) {
    if (!seen.has(d.id)) { seen.add(d.id); result.push(d) }
  }
  return result
})

// ── Active dashboard helpers ──────────────────────────────────────────────────

const activeDashboard = computed(() => dashboardStore.activeDashboard)

// D242: the assistant in the header opens this dashboard's own thread while one is on screen,
// and says which one it is talking about.
const assistantSurface = useAssistantSurface()
watch(activeDashboard, (d) => {
  assistantSurface.set(d
    ? { kind: 'dashboard', id: (d as any).id, label: resolveI18n((d as any).name, currentLocale()) || 'Dashboard' }
    : null)
}, { immediate: true })

// ── Conversational dashboard (D193/D211) ─────────────────────────────────────
// The chat rail lives in @bono-packages/dashboard; this view only says where the ask goes. saas-api
// relays it to the agent, which composes widgets on this very dashboard; on a change the dashboard
// is read again so the grid shows it.
async function askDashboard(question: string): Promise<DashboardAskResult> {
  if (!activeDashboard.value) return { changed: false, message: 'Open a dashboard first.' }
  try {
    const result = await skillsHttp.post<{ changed: boolean; action?: string; message?: string }>(
      `${skillsApiBase()}/dashboards/${(activeDashboard.value as any).id}/ask`,
      { question },
    )
    return { changed: Boolean(result?.changed), action: result?.action, message: result?.message }
  } catch (e) {
    // A refusal is an answer, not a crash: the pane says it in the thread.
    const said = e instanceof SkillsHttpError ? e.detail : 'That did not work.'
    return { changed: false, action: 'NOTHING', message: said }
  }
}

async function onAsked(result: DashboardAskResult) {
  if (!result.changed || !activeDashboard.value) return
  await dashboardStore.fetchDashboard((activeDashboard.value as any).id)
  if (dashboardStore.activeDashboard) fetchAllWidgets(dashboardStore.activeDashboard as unknown as Dashboard)
}

function isOwnDashboard(d: Dashboard): boolean {
  return d.createdBy === currentUserId && (d as any).createdByPrincipal !== 'OPS'
}

const canEdit = computed(() => {
  if (!activeDashboard.value) return false
  if (isOwnDashboard(activeDashboard.value as Dashboard)) return true
  if (isSuperAdmin.value && (activeDashboard.value as Dashboard).sharingMode !== 'PRIVATE') return true
  return false
})

const canPersonalize = computed(() =>
  !!activeDashboard.value && !canEdit.value
)

const canDelete = computed(() =>
  !!activeDashboard.value && isOwnDashboard(activeDashboard.value as Dashboard)
)

const canPublish = computed(() =>
  !!activeDashboard.value && isOwnDashboard(activeDashboard.value as Dashboard)
)

async function onDeleteDashboard(id: string) {
  await dashboardStore.deleteDashboard(id)
  mode.value = 'view'
  await dashboardStore.fetchDashboards()
  setInitialActiveDashboard()
  if (activeDashboard.value) fetchAllWidgets(activeDashboard.value as unknown as Dashboard)
}

function setInitialActiveDashboard() {
  const first = availableDashboards.value[0]
  if (first) dashboardStore.activeDashboard = first as any
}

// ── Mount ─────────────────────────────────────────────────────────────────────

onMounted(async () => {
  // Don't load dashboards/widgets/entities before a company is selected
  // (super admin flow) — these need company context and would fail.
  if (!hasSelectedCompany()) return
  await Promise.all([
    dashboardStore.fetchDashboards(),
    widgetStore.fetchWidgets(),
    queryBuilderStore.fetchEntities(),
    user.fetchUsers(),
  ])
  setInitialActiveDashboard()
  // A Skill's deliverable links here by id (?dashboard=…); open that one rather than the first.
  const asked = route.query.dashboard
  if (typeof asked === 'string' && asked) {
    showMainDashboard.value = false
    await onSwitch(asked)
    return
  }
  if (activeDashboard.value) fetchAllWidgets(activeDashboard.value as unknown as Dashboard)
})

// ── Toggle main / custom ──────────────────────────────────────────────────────

function onToggleMainDashboard() {
  dashboardStore.clearWidgetData()
  showMainDashboard.value = !showMainDashboard.value
  if (!showMainDashboard.value && activeDashboard.value) {
    fetchAllWidgets(activeDashboard.value as unknown as Dashboard)
  }
}

// ── Navigation ────────────────────────────────────────────────────────────────

async function onSwitch(dashboardId: string) {
  dashboardStore.clearWidgetData()
  await dashboardStore.fetchDashboard(dashboardId)
  if (activeDashboard.value) fetchAllWidgets(activeDashboard.value as unknown as Dashboard)
}

async function onEditDashboard(dashboardId: string) {
  dashboardStore.clearWidgetData()
  await dashboardStore.fetchDashboard(dashboardId)
  if (!activeDashboard.value) return
  fetchAllWidgets(activeDashboard.value as unknown as Dashboard)

  // OPS dashboards and non-owned dashboards: personalize first (unless superAdmin)
  if (!isOwnDashboard(activeDashboard.value as Dashboard)) {
    if (isSuperAdmin.value && (activeDashboard.value as Dashboard).sharingMode !== 'PRIVATE') {
      mode.value = 'edit'
      return
    }
    await onPersonalize()
    return
  }

  mode.value = 'edit'
}

async function onSaveLayout(payload: any) {
  if (!activeDashboard.value) return
  await dashboardStore.saveLayout(activeDashboard.value.id, payload)
  mode.value = 'view'
}

async function onRemoveWidget(dwId: string) {
  if (!activeDashboard.value) return
  await dashboardStore.removeWidget(activeDashboard.value.id, dwId)
}

// ── Widget library ────────────────────────────────────────────────────────────

async function onAddWidget(widget: any) {
  if (!activeDashboard.value) return
  const oldIds = new Set(activeDashboard.value.widgets.map(dw => dw.id))
  const updated = await dashboardStore.addWidget(activeDashboard.value.id, widget.id)
  if (updated) {
    const newDw = updated.widgets.find(dw => !oldIds.has(dw.id))
    if (newDw) {
      dashboardEditRef.value?.addWidget(newDw)
      fetchWidget(newDw.id, newDw.widgetId)
      activeDashboard.value?.widgets.push(newDw)
    }
  }
}

async function onCreateAndAdd(payload: any) {
  const created = await widgetStore.createWidget(payload)
  if (created && activeDashboard.value) {
    const oldIds = new Set(activeDashboard.value.widgets.map(dw => dw.id))
    const updated = await dashboardStore.addWidget(activeDashboard.value.id, created.id)
    if (updated) {
      const newDw = updated.widgets.find(dw => !oldIds.has(dw.id))
      if (newDw) {
        dashboardEditRef.value?.addWidget(newDw)
        fetchWidget(newDw.id, newDw.widgetId)
        activeDashboard.value?.widgets.push(newDw)
      }
    }
  }
}

async function onDeleteWidget(widgetId: string) {
  await widgetStore.deleteWidget(widgetId)
  await widgetStore.fetchWidgets()
}

// ── Edit widget ───────────────────────────────────────────────────────────────

const editingDw = ref<DashboardWidget | null>(null)

async function onEditWidget(dw: DashboardWidget) {
  // If dashboard is OPS or not owned, personalize first and edit the copy's widget
  // Exception: superAdmin can edit directly on non-PRIVATE dashboards
  if (activeDashboard.value && !isOwnDashboard(activeDashboard.value as Dashboard)) {
    const isDirectEditable = isSuperAdmin.value && (activeDashboard.value as Dashboard).sharingMode !== 'PRIVATE'
    if (!isDirectEditable) {
      const originalWidgetId = dw.widget.id
      await onPersonalize()
      if (!activeDashboard.value) return
      const copiedDw = activeDashboard.value.widgets.find(
        w => (w.widget as any).copiedFromId === originalWidgetId
      )
      if (!copiedDw) return
      dw = copiedDw as DashboardWidget
    }
  }

  resolvedFields.value = await queryBuilderStore.fetchDeepFields(dw.widget.entityId)
  editingDw.value = dw
}

async function onWidgetEditSave(payload: any) {
  if (!editingDw.value) return
  const updated = await widgetStore.updateWidget(editingDw.value.widget.id, payload)
  if (updated) {
    dashboardEditRef.value?.updateWidget(editingDw.value.id, updated)
    fetchWidget(editingDw.value.id, updated.id)
  }
  editingDw.value = null
}

// ── Create dashboard ──────────────────────────────────────────────────────────

async function createDashboard() {
  const name = cleanNameMap()
  if (!Object.keys(name).length) return
  // Create = name only → private to creator. Sharing happens later via the access panel.
  const created = await dashboardStore.createDashboard({ name })
  showCreateDialog.value = false
  newDashboardName.value = {}

  if (created) {
    await dashboardStore.fetchDashboard(created.id)
    mode.value = 'edit'
  }
}

// ── Personalize OPS dashboard ─────────────────────────────────────────────────

async function onPersonalize() {
  if (!activeDashboard.value) return
  const dashboardId = activeDashboard.value.id

  // Reuse existing copy instead of creating a duplicate (copy is always USER principal)
  const existing = dashboardStore.dashboards.find(
    d => d.copiedFromId === dashboardId &&
         d.createdBy === currentUserId &&
         (d as any).createdByPrincipal !== 'OPS'
  )
  if (existing) {
    dashboardStore.clearWidgetData()
    await dashboardStore.fetchDashboard(existing.id)
    if (activeDashboard.value) fetchAllWidgets(activeDashboard.value as unknown as Dashboard)
    mode.value = 'edit'
    return
  }

  const copy = await dashboardStore.copyDashboard(dashboardId)
  if (copy) {
    await dashboardStore.fetchDashboard(copy.id)
    if (activeDashboard.value) fetchAllWidgets(activeDashboard.value as unknown as Dashboard)
    mode.value = 'edit'
  }
}

async function onFetchSuggestions(entityName: string, field: ResolvedField, search: string) {
  return queryBuilderStore.fetchSuggestions(entityName, (field as any).field, search)
}

// ── Share dashboard (visibility via access rules → CA_HOME slot) ───────────────

const showPublish = ref(false)
const publishCurrentSlots = ref<{ slotKey: string; paramMapping?: Record<string, string> }[]>([])

const publishRules = computed<AccessRule[]>(
  () => ((activeDashboard.value as any)?.accessRuleList ?? []) as AccessRule[]
)
const isPublished = computed(() => publishCurrentSlots.value.length > 0)

async function enterPublishMode() {
  if (!activeDashboard.value) return
  publishCurrentSlots.value = await dashboardStore.fetchCurrentSlots(activeDashboard.value.id)
  await onApplicationsSelected(['CARBON_ACCOUNTING'])
  const seen = new Set<string>()
  for (const rule of (activeDashboard.value as any).accessRuleList ?? []) {
    if ((rule.principalType === 'USER' || rule.principalType === 'ACCOUNT') && !seen.has(rule.principalType)) {
      seen.add(rule.principalType)
      await onFetchPrincipalValues('CARBON_ACCOUNTING', rule.principalType)
    }
  }
  showPublish.value = true
}

async function onPublish(accessRules: AccessRule[]) {
  if (!activeDashboard.value) return
  const result = await dashboardStore.publishDashboard(activeDashboard.value.id, {
    slots: [{ slotKey: CA_SLOT_KEY, accessRules }],
    propagateToWidgets: true,
  })
  if (result) publishCurrentSlots.value = result
  await dashboardStore.fetchDashboards()
  showPublish.value = false
}

async function onUnpublish() {
  if (!activeDashboard.value) return
  await dashboardStore.unpublishAll(activeDashboard.value.id)
  publishCurrentSlots.value = []
  await dashboardStore.fetchDashboards()
  showPublish.value = false
}

</script>

<template>
  <div v-if="user.user?.id" class="grid grid-cols-1 gap-6">

    <!-- ── Top bar ────────────────────────────────────────────────────────── -->
    <div class="flex items-center justify-between min-h-[32px]">
      <!-- Create button: only when no dashboards exist -->
      <button
        v-if="!dashboardStore.loading && availableDashboards.length === 0"
        data-testid="ca-dashboard:create-custom:button"
        class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-neautrals-900 text-white rounded-lg hover:bg-neautrals-800 transition-colors"
        @click="showCreateDialog = true"
      >
        <i class="ri-add-line"></i> Create Custom Dashboard
      </button>
      <div v-else></div>

      <!-- Switch: only when dashboards exist -->
      <div
        v-if="availableDashboards.length > 0"
        class="flex items-center gap-2 text-xs font-semibold select-none"
      >
        <span :class="!showMainDashboard ? 'text-neautrals-900' : 'text-neautrals-400'">Custom</span>
        <button
          data-testid="ca-dashboard:main-toggle:toggle"
          class="relative w-10 h-5 rounded-full transition-colors focus:outline-none"
          :class="showMainDashboard ? 'bg-primary-600' : 'bg-neautrals-300'"
          @click="onToggleMainDashboard"
        >
          <span
            class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform"
            :class="showMainDashboard ? 'translate-x-5' : 'translate-x-0'"
          ></span>
        </button>
        <span :class="showMainDashboard ? 'text-neautrals-900' : 'text-neautrals-400'">Main Dashboard</span>
      </div>
    </div>

    <!-- ── Dashboard (view mode) ──────────────────────────────────────────── -->
    <DashboardView
      v-if="mode === 'view' && activeDashboard && !showMainDashboard"
      :dashboard="activeDashboard as any"
      :available-dashboards="availableDashboards as any"
      :widget-data="dashboardStore.widgetData as any"
      :widget-loading="dashboardStore.widgetLoading"
      :widget-errors="dashboardStore.widgetErrors"
      :can-edit="canEdit"
      :can-personalize="canPersonalize"
      :can-publish="canPublish"
      :own-user-id="currentUserId"
      :allow-create="true"
      :transparent="true"
      @switch="onSwitch"
      @edit="mode = 'edit'"
      @personalize="onPersonalize"
      @publish="enterPublishMode"
      @new-dashboard="showCreateDialog = true"
      @edit-dashboard="onEditDashboard"
      @fetch-widget="({ dwId }) => fetchWidget(dwId, (activeDashboard!.widgets.find(dw => dw.id === dwId) as any)?.widgetId ?? '')"
      @retry-widget="({ dwId }) => fetchWidget(dwId, (activeDashboard!.widgets.find(dw => dw.id === dwId) as any)?.widgetId ?? '')"
    >
      <template #chat>
        <DashboardChat :dashboard="activeDashboard as any" :thread-for="dashboardThread" :ask="askDashboard" @changed="onAsked" />
      </template>
    </DashboardView>

    <!-- ── Dashboard (edit mode) ──────────────────────────────────────────── -->
    <div v-else-if="mode === 'edit' && activeDashboard && !showMainDashboard">
      <!-- Back bar -->
      <div class="flex items-center gap-2 mb-3">
        <button
          data-testid="ca-dashboard:edit-back:button"
          class="flex items-center gap-1.5 text-xs font-semibold text-neautrals-500 hover:text-neautrals-900 transition-colors"
          @click="mode = 'view'"
        >
          <i class="ri-arrow-left-line"></i>
          Back
        </button>
        <span class="text-neautrals-300">/</span>
        <span class="text-xs font-semibold text-neautrals-900 truncate">{{ resolveI18n(activeDashboard.name, currentLocale()) }}</span>
        <span class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary-50 text-primary-600 border border-primary-200">
          Editing
        </span>
      </div>

      <DashboardEdit
        :key="activeDashboard.id"
        ref="dashboardEditRef"
        :dashboard="activeDashboard as any"
        :widget-data="dashboardStore.widgetData as any"
        :widget-loading="dashboardStore.widgetLoading"
        :widget-errors="dashboardStore.widgetErrors"
        :saving="dashboardStore.saving"
        :is-super-admin="isSuperAdmin"
        :can-delete="canDelete"
        @save-layout="onSaveLayout"
        @discard="mode = 'view'"
        @open-library="libraryOpen = true"
        @remove-widget="onRemoveWidget"
        @edit-widget="onEditWidget"
        @delete-dashboard="onDeleteDashboard"
        @rename="onRenameDashboard as any"
        @fetch-widget="({ dwId }) => fetchWidget(dwId, (activeDashboard!.widgets.find(dw => dw.id === dwId) as any)?.widgetId ?? '')"
      >
        <template #chat>
          <DashboardChat :dashboard="activeDashboard as any" :thread-for="dashboardThread" :ask="askDashboard" editing @changed="onAsked" />
        </template>
      </DashboardEdit>
    </div>

    <!-- ── Widget Library Drawer ──────────────────────────────────────────── -->
    <WidgetLibraryDrawer
      v-if="activeDashboard"
      :open="libraryOpen"
      :widgets="widgetStore.widgets as any"
      :added-widget-ids="activeDashboard.widgets.map(dw => dw.widgetId)"
      :entities="queryBuilderStore.entities as any"
      :resolved-fields="resolvedFields as any"
      :execute-sample-query="executeSampleQuery"
      :current-user-id="currentUserId"
      :fetch-suggestions="onFetchSuggestions as any"
      @close="libraryOpen = false"
      @add-widget="onAddWidget"
      @create-and-add="onCreateAndAdd"
      @entity-change="onEntityChange"
      @delete-widget="onDeleteWidget"
    />

    <!-- ── Edit widget dialog ────────────────────────────────────────────────── -->
    <div
      v-if="editingDw"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      @click.self="editingDw = null"
    >
      <div class="bg-white rounded-xl shadow-2xl overflow-hidden" style="width: 90vw; max-width: 1400px; height: 90vh;">
        <WidgetBuilder
          :entities="queryBuilderStore.entities as any"
          :resolved-fields="resolvedFields as any"
          :initial-widget="editingDw.widget as any"
          :disable-type-change="true"
          :execute-sample-query="executeSampleQuery"
          :fetch-suggestions="onFetchSuggestions as any"
          @entity-change="onEntityChange"
          @save="onWidgetEditSave"
          @cancel="editingDw = null"
        />
      </div>
    </div>

    <!-- ── Create Dashboard dialog ────────────────────────────────────────── -->
    <div
      v-if="showCreateDialog"
      data-testid="ca-dashboard-create:modal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      @click.self="showCreateDialog = false"
    >
      <div class="bg-white rounded-xl shadow-xl p-6 w-[400px] flex flex-col gap-4">
        <div class="flex items-center justify-between">
          <span class="text-sm font-bold text-neautrals-900">New Dashboard</span>
          <button
            data-testid="ca-dashboard-create:close:button"
            class="text-neautrals-400 hover:text-neautrals-900 cursor-pointer"
            @click="showCreateDialog = false"
          >
            <i class="ri-close-line"></i>
          </button>
        </div>

        <!-- Name -->
        <div>
          <label class="text-[11px] font-bold text-neautrals-700 block mb-1">Name <span class="text-red-400">*</span> <span class="font-normal text-neautrals-400">(per language)</span></label>
          <div data-testid="ca-dashboard-create:name:input">
            <I18nTextInput v-model="newDashboardName" placeholder="Dashboard name…" />
          </div>
        </div>

        <p class="text-[10px] text-neautrals-400 -mt-1">
          Starts private to you. Share it later from the dashboard menu.
        </p>

        <div class="flex justify-end gap-2 pt-1">
          <button
            data-testid="ca-dashboard-create:cancel:button"
            class="px-4 py-2 text-xs font-semibold text-neautrals-600 border border-neautrals-200 rounded-lg hover:bg-neautrals-50 cursor-pointer"
            @click="showCreateDialog = false"
          >Cancel</button>
          <button
            data-testid="ca-dashboard-create:submit"
            class="px-4 py-2 text-xs font-semibold bg-neautrals-900 text-white rounded-lg hover:bg-neautrals-800 disabled:opacity-40 cursor-pointer"
            :disabled="!Object.values(newDashboardName).some(v => v && v.trim())"
            @click="createDashboard"
          >Create</button>
        </div>
      </div>
    </div>

    <!-- ── Legacy CompanyDashboard ───────────────────────────────────────── -->
    <CompanyDashboard v-if="!activeDashboard || showMainDashboard" />

    <!-- ── Share dashboard modal ─────────────────────────────────────────── -->
    <div
      v-if="showPublish && activeDashboard"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      @click.self="showPublish = false"
    >
      <div class="w-[420px] max-h-[85vh] overflow-auto">
        <!-- TODO(pre-existing): DashboardAccessPanel disabled until @bono-packages/dashboard
             is updated (see import note at the top of this file).
        <DashboardAccessPanel
          :application="'CARBON_ACCOUNTING'"
          :current-rules="publishRules"
          :published="isPublished"
          :publishing="dashboardStore.saving"
          :available-apps="accessControlStore.apps as any"
          :principal-value-options="accessControlStore.principalValueOptions as any"
          :role-options="accessControlStore.roleOptions as any"
          @publish="onPublish"
          @unpublish="onUnpublish"
          @applications-selected="onApplicationsSelected"
          @fetch-principal-values="onFetchPrincipalValues"
        />
        -->
      </div>
    </div>

  </div>
</template>